import { BehaviorSubject, catchError, EMPTY, finalize, Observable, of, tap, timer } from 'rxjs';
import { computed, Injectable, signal, inject } from '@angular/core';

import { ToastService } from '@app/services/toast.service';
import { StorageService } from '@app/services/storage.service';
import { LoadingService } from '@app/services/loading.service';
import { JsonRpcService } from '@app/services/json-rpc.service';
import { ApiMethod } from '@app/interfaces/api-request-body.interface';
import { ConferenceLog } from '@app/interfaces/conference-log.interface';
import { ConferenceRoom } from '@app/interfaces/conference-room.interface';
import { ConferenceCall } from '@app/interfaces/conference-call.interface';

@Injectable({
  providedIn: 'root',
})
export class ConferencesService {
  private toastSrv = inject(ToastService);
  private storage = inject(StorageService);
  private jsonrpc = inject(JsonRpcService);
  private loadingSrv = inject(LoadingService);

  // These keys used to store the conference room updated in the storage.
  confRoomKey = 'conference_room';
  apiError = signal<object | null>(null);

  isLoadingRooms$ = new BehaviorSubject(false);
  selectedRoomId = signal<number | null>(null);
  readonly rooms = signal<ConferenceRoom[]>([]);
  readonly selectedRoom = computed(() => {
    return this.rooms().find((room) => room.ID === this.selectedRoomId());
  });

  selectedCallId = signal<number | null>(null);
  readonly calls = signal<ConferenceCall[]>([]);
  readonly selectedCall = computed(() => {
    return this.calls().find((call) => call.ID === this.selectedCallId());
  });

  async assignLastConference(): Promise<void> {
    this.selectedRoomId.set(await this.storage.get(this.confRoomKey));
  }

  selectRoom(confroom: number | null) {
    this.selectedRoomId.set(confroom);
    this.storage.set(this.confRoomKey, confroom);
  }

  selectCall(callId: number | null) {
    this.selectedCallId.set(callId);
  }

  getConferenceRooms(): Observable<ConferenceRoom[]> {
    this.isLoadingRooms$.next(true);
    return this.jsonrpc.request<ConferenceRoom[]>(ApiMethod.getConferenceRooms).pipe(
      tap((rooms) => {
        this.rooms.set(rooms);
        if (rooms.length === 1) {
          this.selectedRoomId.set(rooms[0].ID);
        }
      }),
      catchError((error) => {
        this.toastSrv.showToast('Error get conference rooms: ' + error.message);
        return EMPTY;
      }),
      finalize(() => this.isLoadingRooms$.next(false)),
    );
  }

  getConferenceCalls(confroomId: number | null, offset: number, limit: number): Observable<ConferenceCall[]> {
    return this.jsonrpc
      .request<ConferenceCall[]>(ApiMethod.getConferenceCalls, {
        ...(confroomId && { confroomId }),
        offset,
        limit,
      })
      .pipe(
        tap((calls) => {
          this.calls.set(offset === 0 ? calls : this.calls().concat(calls));
        }),
        catchError((error) => {
          this.apiError.set(error);
          timer(5000).subscribe(() => this.apiError.set(null));
          this.toastSrv.showToast('Error get conference calls: ' + error.message);
          return EMPTY;
        }),
      );
  }

  getConferenceLogs(confroom: string | null, offset: number, limit: number): Observable<ConferenceLog[]> {
    return this.jsonrpc
      .request<ConferenceLog[]>(ApiMethod.getConferenceLogs, {
        ...(confroom && { confroom }),
        offset,
        limit,
      })
      .pipe(
        catchError((error) => {
          this.apiError.set(error);
          timer(5000).subscribe(() => this.apiError.set(null));
          this.toastSrv.showToast('Error get conference logs: ' + error.message);
          return EMPTY;
        }),
      );
  }

  createConferenceCall(
    confroomId: number,
    startsAt: number,
    duration: number,
    description?: string,
  ): Observable<ConferenceCall> {
    return this.loadingSrv
      .runWithLoading<ConferenceCall>(
        this.jsonrpc.request(ApiMethod.createConferenceCall, {
          confroomId,
          startsAt,
          duration,
          ...(description && { description }),
        }),
      )
      .pipe(
        catchError((error) => {
          this.toastSrv.showToast('Error create conference: ' + error.message);
          return EMPTY;
        }),
        tap((newCall) => {
          this.toastSrv.showToast(`The conference was successfully created!`);
          const calls = [newCall].concat(this.calls());
          this.calls.set(calls);
        }),
      );
  }

  getConferenceCall(id: number): Observable<ConferenceCall> {
    return this.jsonrpc
      .request<ConferenceCall>(ApiMethod.getConferenceCall, {
        id,
      })
      .pipe(
        catchError(() => {
          this.toastSrv.showToast('Error get conference');
          return EMPTY;
        }),
        tap((call) => {
          this.calls.update((calls) => [...calls.map((c) => (c.ID === call.ID ? call : c))]);
        }),
      );
  }

  deleteCall(call: ConferenceCall): Observable<void> {
    return this.loadingSrv
      .runWithLoading<void>(this.jsonrpc.request(ApiMethod.delConferenceCall, { id: call.ID }))
      .pipe(
        catchError((error) => {
          this.toastSrv.showToast('Error delete conference: ' + error.message);
          return EMPTY;
        }),
        tap(() => {
          this.toastSrv.showToast(`The conference was successfully deleted!`);
          const calls = this.calls().filter((obj) => obj.ID !== call.ID);
          this.calls.set(calls);
        }),
      );
  }

  deleteOldCalls(hoursAgo = 72): Observable<number> {
    return this.jsonrpc.request<number>(ApiMethod.delOldConferenceCalls, { hoursAgo }).pipe(
      catchError((error) => {
        console.log('error delete old conference calls', error);
        return of(0);
      }),
    );
  }
}
